import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SEOSchema from '@/components/feature/SEOSchema';
import { articles } from '@/mocks/articles';

const articleCategories = [
  { id: 'all', name: '全部文章' },
  { id: 'beginner', name: '新手入门' },
  { id: 'strategy', name: '玩法技巧' },
  { id: 'guide', name: '游戏指南' },
  { id: 'culture', name: '麻将文化' },
];

export default function Articles() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredArticles = activeCategory === 'all'
    ? articles
    : articles.filter(a => a.category === activeCategory);

  return (
    <>
      <SEOHead
        title="麻将攻略与技巧 - 新手入门、玩法技巧、麻将文化 | 麻将游戏在线玩"
        description="全面的麻将攻略文章：从新手入门教程到高级读牌技巧，从四川麻将规则到麻将历史文化。提升你的麻将水平，成为麻将高手！"
        keywords="麻将攻略,麻将技巧,麻将入门教程,麻将规则,麻将文化,麻将必胜口诀"
        canonicalUrl="https://mahjong-online.com/articles"
        ogTitle="麻将攻略与技巧 - 从新手到高手 | 麻将游戏在线玩"
        ogDescription="全面的麻将攻略文章，从新手入门教程到高级读牌技巧，提升你的麻将水平！"
        ogUrl="https://mahjong-online.com/articles"
      />
      <SEOSchema
        type="WebPage"
        data={{
          name: '麻将攻略文章',
          description: '麻将游戏攻略与技巧文章合集，涵盖新手入门、玩法技巧、游戏指南和麻将文化',
          url: 'https://mahjong-online.com/articles',
        }}
      />

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: '麻将攻略' },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-4xl font-heading text-foreground-950">
                麻将攻略与技巧
              </h1>
              <p className="mt-3 text-sm md:text-base text-foreground-600">
                从新手到高手，全方位提升你的麻将水平
              </p>
            </div>

            <div className="flex items-center justify-center flex-wrap gap-2 mb-10">
              {articleCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50 shadow-sm'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="space-y-4 md:space-y-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/articles/${article.id}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="group flex flex-col sm:flex-row gap-4 md:gap-6 p-4 md:p-5 bg-background-100 rounded-lg hover:shadow-md transition-all duration-200 cursor-pointer"
                >
                  <div className="w-full sm:w-48 md:w-56 h-40 sm:h-32 flex-shrink-0 rounded-md overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-0.5 bg-accent-100 text-accent-800 rounded-full whitespace-nowrap">
                          {article.categoryName}
                        </span>
                        <span className="text-xs text-foreground-400">{article.readTime} 阅读</span>
                      </div>
                      <h2 className="text-base md:text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line"></i> {article.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i> {article.date}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}